<template>
  <div class="detail-root">
    <header class="detail-header">
      <ONEButton
        label="Back"
        icon="pi pi-arrow-left"
        class="p-button-text p-button-sm"
        @click="router.push('/sop-documents')"
      />
    </header>

    <div v-if="loading" class="detail-skeleton">
      <ONESkeleton height="2rem" class="mb-3" />
      <ONESkeleton height="1rem" width="40%" class="mb-4" />
      <ONESkeleton height="400px" />
    </div>

    <div v-else-if="!item" class="empty-state">
      <i class="pi pi-inbox text-4xl text-slate-300"></i>
      <p class="text-slate-500 mt-2">Document not found</p>
    </div>

    <div v-else class="detail-content">
      <!-- Meta -->
      <div class="meta-row">
        <ONETag :value="item.Category" severity="warning" />
        <span v-if="item.Version" class="meta-badge">v{{ item.Version }}</span>
        <span v-if="item.DepartmentName" class="meta-dept">
          <i class="pi pi-building"></i> {{ item.DepartmentName }}
        </span>
        <span class="meta-date">
          <i class="pi pi-calendar"></i> {{ formatDate(item.CreatedDate) }}
        </span>
        <span v-if="item.CreatedByName" class="meta-author">
          <i class="pi pi-user"></i> {{ item.CreatedByName }}
        </span>
      </div>

      <h1 class="detail-title">{{ item.Title }}</h1>

      <p v-if="item.Description" class="plain-body">{{ item.Description }}</p>

      <!-- Document viewer -->
      <div v-if="item.FullFilePath || item.FilePath" class="viewer-section">
        <div class="viewer-header">
          <i class="pi pi-file text-teal-600"></i>
          <span>Document Preview</span>
          <a :href="item.FullFilePath || item.FilePath" target="_blank" class="download-btn">
            <i class="pi pi-download"></i> Download
          </a>
        </div>

        <!-- PDF -->
        <iframe
          v-if="isPDF(item.FullFilePath || item.FilePath)"
          :src="item.FullFilePath || item.FilePath"
          class="doc-viewer"
          frameborder="0"
        />

        <!-- Office docs (PPTX, DOCX, XLSX) via MS Office Online -->
        <iframe
          v-else-if="isOfficeDoc(item.FullFilePath || item.FilePath)"
          :src="officeViewerUrl(item.FullFilePath || item.FilePath)"
          class="doc-viewer"
          frameborder="0"
          allowfullscreen
        />

        <!-- Image -->
        <img
          v-else-if="isImage(item.FullFilePath || item.FilePath)"
          :src="item.FullFilePath || item.FilePath"
          class="image-viewer"
          :alt="item.Title"
        />

        <!-- Other — just show download -->
        <div v-else class="no-preview">
          <i class="pi pi-file-o text-3xl text-slate-300"></i>
          <p class="text-slate-500 mt-2">No preview available</p>
        </div>
      </div>

      <div v-if="canManageSOP" class="admin-actions">
        <ONEButton
          label="Edit"
          icon="pi pi-pencil"
          class="p-button-outlined p-button-sm"
          @click="router.push(`/sop-documents/${item.Id}/edit`)"
        />
        <ONEButton
          :label="item.IsActive ? 'Deactivate' : 'Activate'"
          :icon="item.IsActive ? 'pi pi-eye-slash' : 'pi pi-eye'"
          :class="item.IsActive ? 'p-button-outlined p-button-warning p-button-sm' : 'p-button-outlined p-button-success p-button-sm'"
          @click="toggleActive"
        />
        <ONEButton
          label="Delete"
          icon="pi pi-trash"
          class="p-button-outlined p-button-danger p-button-sm"
          @click="showDeleteConfirm = true"
        />
      </div>

      <!-- Delete Confirm -->
      <div v-if="showDeleteConfirm" class="confirm-overlay" @click.self="showDeleteConfirm = false">
        <div class="confirm-dialog">
          <div class="confirm-icon"><i class="pi pi-exclamation-triangle"></i></div>
          <h3 class="confirm-title">Delete SOP Document?</h3>
          <p class="confirm-body">
            "<strong>{{ item.Title }}</strong>" will be permanently removed.
          </p>
          <div class="confirm-actions">
            <button class="conf-cancel-btn" @click="showDeleteConfirm = false">Cancel</button>
            <button class="conf-delete-btn" :disabled="deleting" @click="doDelete">
              <i v-if="deleting" class="pi pi-spin pi-spinner"></i>
              <span v-else>Delete</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import moment from 'moment'
import { getSOPDocumentById, activateSOPDocument, deleteSOPDocument, getDashboardUserContext } from '../../services/dashboardService'

const router = useRouter()
const route = useRoute()

const loading = ref(true)
const item = ref<any>(null)
const showDeleteConfirm = ref(false)
const deleting = ref(false)

const ctx = getDashboardUserContext()
const canManageSOP = ctx.canManageSOP

async function toggleActive() {
  if (!item.value) return
  const newStatus = !item.value.IsActive
  const ok = await activateSOPDocument(item.value.Id, newStatus, ctx.userId)
  if (ok) item.value.IsActive = newStatus
}

async function doDelete() {
  if (!item.value) return
  deleting.value = true
  const ok = await deleteSOPDocument(item.value.Id, ctx.userId)
  deleting.value = false
  if (ok) router.push('/sop-documents')
}

function ext(path?: string) {
  if (!path) return ''
  return path.split('.').pop()?.toLowerCase() ?? ''
}
function isPDF(path?: string) { return ext(path) === 'pdf' }
function isOfficeDoc(path?: string) {
  return ['ppt', 'pptx', 'doc', 'docx', 'xls', 'xlsx'].includes(ext(path))
}
function isImage(path?: string) {
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext(path))
}
function officeViewerUrl(path: string) {
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(path)}`
}

function formatDate(d: any) {
  if (!d) return ''
  const m = moment(d, ['DD-MM-YYYY', moment.ISO_8601], true)
  return m.isValid() ? m.format('DD MMM YYYY') : moment(d).format('DD MMM YYYY')
}

onMounted(async () => {
  const id = Number(route.params.id)
  if (id) item.value = await getSOPDocumentById(id)
  loading.value = false
})
</script>

<style scoped>
.detail-root { min-height: 100vh; background: var(--one-rule-soft); padding: 1.5rem; font-family: 'Inter', sans-serif; }
.detail-header { margin-bottom: 1rem; }
.detail-skeleton { max-width: 900px; margin: 0 auto; }
.empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4rem; text-align: center; }
.detail-content { max-width: 900px; margin: 0 auto; background: #fff; border: 1px solid var(--one-rule); border-radius: 12px; padding: 2rem; box-shadow: 0 1px 4px rgba(0,0,0,0.04); }
.meta-row { display: flex; flex-wrap: wrap; align-items: center; gap: 0.75rem; margin-bottom: 1rem; font-size: 0.82rem; color: var(--one-muted); }
.meta-badge { background: var(--one-rule-soft); border: 1px solid var(--one-rule); border-radius: 8px; padding: 2px 10px; font-size: 0.75rem; font-weight: 600; color: #475569; }
.meta-dept, .meta-date, .meta-author { display: inline-flex; align-items: center; gap: 4px; }
.detail-title { font-size: 1.75rem; font-weight: 700; color: #0f172a; margin: 0 0 1rem 0; line-height: 1.3; }
.plain-body { font-size: 0.95rem; line-height: 1.75; color: #334155; white-space: pre-wrap; margin-bottom: 1.5rem; }
.viewer-section { border: 1px solid var(--one-rule); border-radius: 10px; overflow: hidden; margin-top: 1rem; }
.viewer-header { display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem 1rem; background: #f8fafc; border-bottom: 1px solid var(--one-rule); font-size: 0.875rem; font-weight: 600; color: #475569; }
.viewer-header .download-btn { margin-left: auto; color: #0d9488; text-decoration: none; font-size: 0.8rem; display: flex; align-items: center; gap: 4px; }
.doc-viewer { width: 100%; height: 600px; display: block; }
.image-viewer { width: 100%; max-height: 500px; object-fit: contain; display: block; padding: 1rem; }
.no-preview { display: flex; flex-direction: column; align-items: center; padding: 3rem; }
.admin-actions { border-top: 1px solid var(--one-rule-soft); padding-top: 1rem; margin-top: 1.5rem; display: flex; gap: 0.5rem; flex-wrap: wrap; }
.confirm-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.55); backdrop-filter: blur(3px); display: flex; align-items: center; justify-content: center; z-index: 1100; }
.confirm-dialog { background: #fff; border-radius: 14px; padding: 2rem; max-width: 420px; width: 90%; box-shadow: 0 20px 50px rgba(0,0,0,0.2); text-align: center; }
.confirm-icon { font-size: 2.5rem; color: #f59e0b; margin-bottom: 0.75rem; }
.confirm-title { font-size: 1.1rem; font-weight: 700; color: #0f172a; margin: 0 0 0.5rem 0; }
.confirm-body { font-size: 0.875rem; color: var(--one-muted); line-height: 1.6; margin-bottom: 1.5rem; }
.confirm-actions { display: flex; gap: 0.75rem; justify-content: center; }
.conf-cancel-btn { padding: 8px 24px; border: 1px solid var(--one-rule); border-radius: 8px; background: #f8fafc; color: var(--one-muted); font-size: 0.875rem; cursor: pointer; }
.conf-cancel-btn:hover { background: var(--one-rule-soft); }
.conf-delete-btn { padding: 8px 24px; border: none; border-radius: 8px; background: #dc2626; color: #fff; font-size: 0.875rem; cursor: pointer; display: flex; align-items: center; gap: 6px; }
.conf-delete-btn:hover:not(:disabled) { background: #b91c1c; }
.conf-delete-btn:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
